import { AlertTriangle, CheckCircle2, Loader2, PhoneCall, Video } from "lucide-react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import type { CallError, CallState } from "@/types/call";

/** Seconds → mm:ss (or hh:mm:ss for long consultations). */
export function formatDuration(seconds: number | null): string {
  if (seconds === null || Number.isNaN(seconds)) return "—";
  const total = Math.max(0, Math.round(seconds));
  const hrs = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return hrs > 0 ? `${hrs}:${pad(mins)}:${pad(secs)}` : `${pad(mins)}:${pad(secs)}`;
}

interface CallStatusProps {
  state: CallState;
  error: CallError | null;
  hasSession: boolean;
  endedDuration: number | null;
  /** Whether the patient was ever in the room — decides completed vs missed. */
  patientAttended: boolean;
  /** Rejoining a call already in progress, rather than starting a new one. */
  resuming: boolean;
  onStart: () => void;
  onRetry: () => void;
}

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="card-surface grid place-items-center px-6 py-12 text-center"
      role="status"
      aria-live="polite"
    >
      <div className="max-w-sm">{children}</div>
    </div>
  );
}

function Spinner({ label }: { label: string }) {
  return (
    <Panel>
      <Loader2 className="mx-auto size-6 animate-spin text-primary" aria-hidden />
      <p className="mt-3 text-sm text-muted-foreground">{label}</p>
    </Panel>
  );
}

/** Non-live states: starting, checking, errors and the ended summary. */
export function CallStatus({
  state,
  error,
  hasSession,
  endedDuration,
  patientAttended,
  resuming,
  onStart,
  onRetry,
}: CallStatusProps) {
  if (state === "checking") return <Spinner label="Checking for an active call…" />;
  if (state === "initiating") return <Spinner label="Starting video call…" />;
  if (state === "connectingSdk") {
    // Rejoining is not restarting: the room, the patient and the backend session
    // all survived the doctor navigating away, and saying "connecting" here
    // reads like the consultation was lost and is being set up again.
    return (
      <Spinner
        label={
          resuming
            ? "Rejoining the consultation — the patient is still in the room…"
            : "Connecting to the consultation room…"
        }
      />
    );
  }

  if (state === "idle") {
    return (
      <Panel>
        <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-info/10 text-info">
          <Video className="size-5" aria-hidden />
        </span>
        <p className="mt-4 font-display text-lg font-semibold">Ready when you are</p>
        <p className="mt-1 text-sm text-muted-foreground">
          The patient will be able to join as soon as you start the consultation.
        </p>
        <Button type="button" size="lg" className="mt-5 gap-2" onClick={onStart}>
          <PhoneCall className="size-4" aria-hidden /> Start video call
        </Button>
      </Panel>
    );
  }

  if (state === "error" && error) {
    return (
      <Panel>
        <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-destructive/10 text-destructive">
          <AlertTriangle className="size-5" aria-hidden />
        </span>
        <p className="mt-4 font-display text-lg font-semibold">We couldn't connect</p>
        <p className="mt-1 text-sm text-muted-foreground">{error.message}</p>
        {error.retryable && (
          <Button type="button" variant="outline" className="mt-5" onClick={onRetry}>
            Try again
          </Button>
        )}
      </Panel>
    );
  }

  if (state === "ended") {
    return (
      <Panel>
        <p className="font-display text-xl font-semibold">Call ended</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {endedDuration === null
            ? "This consultation is no longer active."
            : `Duration: ${formatDuration(endedDuration)}`}
        </p>
        {/* Tells the doctor which bucket the appointment just landed in. */}
        <p className="mt-3 text-sm">
          {patientAttended ? (
            <span className="inline-flex items-center gap-1.5 font-medium text-success">
              <CheckCircle2 className="size-4" aria-hidden /> Filed under Completed
            </span>
          ) : (
            <span className="text-muted-foreground">
              The patient never joined, so this stays open until its slot ends.
            </span>
          )}
        </p>
        {!hasSession && null}
        <div className="mt-5 flex flex-wrap justify-center gap-2">
          <Button asChild>
            <Link to="/bookings">Back to bookings</Link>
          </Button>
          <Button type="button" variant="outline" onClick={onRetry}>
            Back to call screen
          </Button>
        </div>
      </Panel>
    );
  }

  return null;
}
